import { Check } from "lucide-react";

import { cn } from "@/lib/utils";
import type { CalendarShellSnapshot } from "../core/shell-types";
import { calendarColor } from "./format";

// The calendar list: colour swatch, name, and a check box mirroring the
// selection (an empty selection means every calendar is shown). Rendered as
// the desktop sidebar (`w-56`) or inside the compact bottom sheet.
export function CalendarList({
  snapshot,
  onToggle,
  compact,
}: {
  snapshot: CalendarShellSnapshot;
  onToggle: (calendarId: string) => void;
  compact: boolean;
}) {
  const allSelected = snapshot.selectedCalendarIds.length === 0;
  const selected = new Set(snapshot.selectedCalendarIds);
  const calendars = [...snapshot.calendars].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
  );

  return (
    <div className={cn("py-2", !compact && "px-2")}>
      {!compact ? (
        <h2 className="px-2.5 pb-1 text-[11px] font-medium uppercase tracking-widest text-muted-foreground/70">
          Calendars
        </h2>
      ) : null}
      {calendars.length === 0 ? (
        <p className="px-2.5 py-2 text-sm text-muted-foreground">No calendars yet.</p>
      ) : null}
      <ul aria-label="Calendars" className={cn(compact && "px-2")}>
        {calendars.map((calendar) => {
          const checked = allSelected || selected.has(calendar.id);
          const color = calendarColor(snapshot, calendar.id);
          return (
            <li key={calendar.id}>
              <label
                data-calendar-id={calendar.id}
                title={checked ? `Hide ${calendar.name}` : `Show ${calendar.name}`}
                className={cn(
                  "flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-left text-sm transition-colors hover:bg-accent/60 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
                  compact ? "min-h-11 py-2" : "py-1.5",
                )}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={checked}
                  onChange={() => onToggle(calendar.id)}
                  aria-label={calendar.name}
                />
                <span
                  aria-hidden
                  className="flex h-4 w-4 shrink-0 items-center justify-center rounded-[5px] border"
                  style={{
                    backgroundColor: checked ? color : "transparent",
                    borderColor: color,
                  }}
                >
                  {checked ? (
                    <Check aria-hidden className="h-3 w-3 text-white" strokeWidth={3} />
                  ) : null}
                </span>
                <span
                  className={cn(
                    "min-w-0 flex-1 truncate",
                    checked ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  {calendar.name}
                </span>
                {calendar.isDefault ? (
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
                    Default
                  </span>
                ) : null}
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
